
<template>
  <div>
    <card-dialog
      v-model="modalVisible"
      max-width="75%"
      :persistent="false"
    >
      <template v-slot:title>
        <v-card-title class="grey-bg px-6 py-3">
          {{ $t('card_title.raw_data') }}
        </v-card-title>
      </template>
      <v-card-text>
        <v-row
          class="mt-3"
          align="center"
          justify="center"
        >
          <v-col cols="auto">
            <v-btn
              icon
              :disabled="event.players.indexOf(player) === 0"
              @click="$emit('previous')"
            >
              <v-icon small>
                fa fa-arrow-left
              </v-icon>
            </v-btn>
          </v-col>
          <v-col
            align="center"
            cols="4"
            class="text-h6"
          >
            {{ player.first_name }} {{ player.last_name }}
          </v-col>
          <v-col cols="auto">
            <v-btn
              icon
              :disabled="event.players.indexOf(player) === event.players.length - 1"
              @click="$emit('next')"
            >
              <v-icon small>
                fa fa-arrow-right
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <div
          v-if="loadingRawData"
          class="text-center"
          style="padding: 76px 0"
        >
          <v-progress-circular
            color="grey lighten-1"
            :size="150"
            indeterminate
            :width="1"
          />
        </div>
        <div
          v-else-if="speedSeries.length === 0 && heartRateLogSeries.length === 0"
          class="text-center"
          style="padding: 76px 0"
        >
          No data available
        </div>
        <div v-else>
          <line-chart
            class="elevation-0"
            :series-override="[{ name: $t('performance.raw.variables.heart_rate'), data: heartRateLogSeries },{ name: $t('performance.raw.variables.speed'), data: speedSeries }]"
            :xaxis="{ type: 'datetime', labels: { datetimeUTC: false } }"
            :yaxis="[ { title: { text: $t('hr_variables.hr_bpm') }, max: 210, min: 0, seriesName: 'Heart Rate' }, { title: { text: $t('gps_variables.speed_unit') }, min: 0, seriesName: 'Speed', opposite: true } ]"
            :tooltip="{
              x: {
                format: 'HH:mm:ss'
              }
            }"
            :animations="{enabled: false}"
            :colors="['#E91E63', '#008FFB']"
            :annotations="heartRateLogAnnotations"
            :height="500"
            :fill="{ type: 'solid', opacity: [1, 1] }"
          />
        </div>
      </v-card-text>
    </card-dialog>
  </div>
</template>

<script>

import CardDialog from '@/components/helpers/CardDialog.vue'
import LineChart from '@/components/charts/LineChart.vue'
import dayjs from 'dayjs'

export default {
  name: 'PerformanceRawDialog',
  components: { LineChart, CardDialog },
  props: {
    player: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      required: true
    },
    loadingRawData: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    heartRateLogSeries() {
      const data = this.player.player_stats.HEART_RATE ?? []

      const timestamps = this.player.player_stats.TIMESTAMP ?? []

      const series = []

      const dateStart = dayjs(this.event.start_at)

      for (const [index, d] of data.entries()) {
        series.push(timestamps[index] ? [timestamps[index] * 1000, d] : {
          x: dateStart.add(index, 'second').format(),
          y: d
        })
      }

      return series
    },
    speedSeries() {
      const data = this.player.player_stats.SPEED ?? []

      const timestamps = this.player.player_stats.TIMESTAMP ?? []

      const series = []

      const dateStart = dayjs(this.event.start_at)

      for (const [index, d] of data.entries()) {
        series.push(timestamps[index] ? [timestamps[index] * 1000, d] : {
          x: dateStart.add(index, 'second').format(),
          y: d
        })
      }

      return series
    },
    heartRateLogAnnotations() {
      const hrProfile = this.player.hr_profile

      if (!hrProfile || !this.player.player_stats.HEART_RATE.length) return undefined

      const method = hrProfile.method

      let addKey = ''

      if (method === 'HRANP') addKey = 'ANPM'

      const zones = [
        { key: 'HRREG', color: '#bfbfbf', label: this.$t('hr_variables.regeneration_zone') },
        { key: 'HRRE', color: '#008FFB', label: this.$t('hr_variables.extensive_aerobic_zone') },
        { key: 'HRAI', color: '#00E396', label: this.$t('hr_variables.intensive_aerobic_zone') },
        { key: 'HRANP', color: '#FEB019', label: this.$t('hr_variables.anaerobic_threshold_zone') },
        { key: 'HRVO2MAX', color: '#FF4560', label: this.$t('hr_variables.vo2_max_zone') }
      ]

      return {
        position: 'back',
        yaxis: zones.map(z => {
          return {
            y: hrProfile.calculations[z.key + addKey].lower,
            y2: hrProfile.calculations[z.key + addKey].upper,
            opacity: 0.6,
            fillColor: z.color,
            borderColor: z.color,
            label: {
              text: z.label,
              offsetY: 17,
              offsetX: -5,
              style: {
                background: 'transparent'
              }
            }
          }
        })
      }
    }

  }
}

</script>

<style scoped>

</style>
